import { z } from "zod";

const suggestionDocumentSchema = z.object({
    id: z.string(),
    invoiceNumber: z.string().nullable(),
    total: z.number(),
    openAmount: z.number().nullable(),
    createdAt: z.coerce.date(),
    counterpartyName: z.string().nullable(),
    lineItemAccountId: z.string().nullable(),
    lineItemAccountIds: z.array(z.string()).optional(),
    hasMultipleLineItemAccounts: z.boolean().optional(),
});

const suggestionTransactionSchema = z.object({
    id: z.number(),
    amount: z.number(),
    date: z.coerce.date(),
    displayDescription: z.string().nullable(),
    bankDescription: z.string(),
});

export const accrualMatchSuggestionDisplaySchema = z.object({
    invoice: suggestionDocumentSchema.nullable().optional(),
    bill: suggestionDocumentSchema.nullable().optional(),
    matchedTransaction: suggestionTransactionSchema.nullable().optional(),
    /**
     * For a transfer suggestion, the accrual account this transaction's leg
     * posts to once the match is confirmed. Absent when the pair cannot be
     * mapped to an account, which is also when confirming it would be skipped.
     */
    accrualAccountId: z.string().nullable().optional(),
});

export type AccrualMatchSuggestionDisplay = z.infer<
    typeof accrualMatchSuggestionDisplaySchema
>;

export function getLineItemAccountIdFromSuggestions(
    suggestions: AccrualMatchSuggestionDisplay[],
): string | null {
    for (const s of suggestions) {
        const doc = s.invoice ?? s.bill;
        if (doc?.lineItemAccountId) {
            return doc.lineItemAccountId;
        }
    }
    return null;
}

export function getDocumentSuggestions(
    suggestions: AccrualMatchSuggestionDisplay[],
): AccrualMatchSuggestionDisplay[] {
    return suggestions.filter((s) => s.invoice ?? s.bill);
}

export function getFirstDocumentSuggestion(
    suggestions: AccrualMatchSuggestionDisplay[],
): AccrualMatchSuggestionDisplay | undefined {
    return suggestions.find((s) => s.invoice ?? s.bill);
}

export function getTransferSuggestion(
    suggestions: AccrualMatchSuggestionDisplay[],
): AccrualMatchSuggestionDisplay | undefined {
    return suggestions.find((s) => s.matchedTransaction);
}
